import { useShow } from "@/contexts/ShowContext";
import { apiFetch } from "@/lib/api";
import Button from "@/components/ui/Button";
import { FiEdit, FiPlus, FiSave, FiTrash, FiX } from "react-icons/fi";
import Loader from "@/components/loader";
import { useState } from "react";
import { Input } from "@/components/ui/shadcn/input";
import { Textarea } from "@/components/ui/shadcn/textarea";
import FilePicker from "@/components/ui/FilePicker";
import useFileUpload from "@/hooks/useFileUpload";
import { useModal } from "@/contexts/ModalContext";
import ConfirmModal from "@/components/modals/ConfirmModal";
import CategoryEditorModal from "@/components/modals/CategoryEditorModal";

export default function SettingsPage() {
  const { show, saveShow, fetchShow } = useShow();
  const [data, setData] = useState({
    asset_categories: show.asset_categories || [],
    wsc_asset_categories: show.wsc_asset_categories || [],
    api_keys: show.api_keys || [],
    name: show.name || "",
    description: show.description || "",
    thumbnail_url: show.thumbnail_url || "",
    thumbnail_key: show.thumbnail_key || "",
  });
  const [updating, setUpdating] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState("");
  const [canSave, setCanSave] = useState(false);
  const { uploadFile, uploadProgress, uploadError } = useFileUpload();
  const { openModal } = useModal();

  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
    setCanSave(true);
  };

  const handleFileChange = (file) => {
    setData({ ...data, thumbnailFile: file });
    setCanSave(true);
  };

  const handleSave = async () => {
    try {
      setUpdating(true);

      if (data.thumbnailFile) {
        const uploadResponse = await uploadFile(data.thumbnailFile);
        data.thumbnail_url = uploadResponse.url;
        data.thumbnail_key = uploadResponse.key;
        delete data.thumbnailFile;
      }

      await saveShow(data);
    } catch (error) {
      console.error(error);
      setLoadingStatus("Failed to save show");
    } finally {
      setUpdating(false);
      setCanSave(false);
    }
  };

  const handleEditCategory = async (category) => {
    const updatedCategory = await openModal(
      <CategoryEditorModal category={category} />,
      "side",
    );
    console.log(updatedCategory);

    if (!updatedCategory) return;

    try {
      setUpdating(true);
      await apiFetch(
        category
          ? `/api/shows/${show._id}/categories/${updatedCategory._id}`
          : `/api/shows/${show._id}/categories`,
        {
          method: category ? "PUT" : "POST",
          body: JSON.stringify(updatedCategory),
        },
      );

      const newShow = await fetchShow(show._id);
      setData(newShow);
    } catch (error) {
      console.error(error);
    } finally {
      setUpdating(false);
    }
  };

  const handleCategoryDelete = async (categoryId) => {
    const confirmed = await openModal(
      <ConfirmModal
        title="Delete Category"
        message="Are you sure you want to delete this category?"
      />,
    );
    if (!confirmed) return;

    try {
      setUpdating(true);
      await apiFetch(`/api/shows/${show._id}/categories/${categoryId}`, {
        method: "DELETE",
      });

      const newShow = await fetchShow(show._id);
      setData(newShow);
    } catch (error) {
      console.error(error);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="flex h-full flex-col relative">
      <div className="flex justify-between items-center px-10 h-20">
        <div className="flex gap-2">
          <h1 className="text-2xl font-bold border-l-4 border-primary pl-4">
            Settings
          </h1>
        </div>
      </div>
      <div className="flex flex-col flex-1 w-full overflow-hidden">
        <div className="flex flex-col px-10 py-4 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-16 max-w-5xl">
            <div className="flex flex-col gap-4">
              <h2 className="text-lg font-bold text-secondary-foreground">
                General
              </h2>
              <div className="flex gap-4 w-full">
                <div className="flex flex-col gap-2 flex-1/3">
                  <label className="text-xs text-secondary-foreground">
                    Thumbnail
                  </label>
                  <FilePicker
                    className="w-full aspect-video"
                    accept="image/*"
                    type="image"
                    name="thumbnailFile"
                    preview={data.thumbnail_url}
                    onChange={handleFileChange}
                  />
                </div>
                <div className="flex flex-col gap-4 flex-2/3">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs text-secondary-foreground">
                      Name
                    </label>
                    <Input
                      name="name"
                      defaultValue={data.name}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-xs text-secondary-foreground">
                      Description
                    </label>
                    <Textarea
                      className="resize-none h-72"
                      name="description"
                      defaultValue={data.description}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="flex items-center justify-between border-b py-2">
                <h2 className="text-lg font-bold text-secondary-foreground">
                  Asset Categories
                </h2>
                <Button
                  id="SettingsPage__addCategory"
                  type="secondary"
                  icon={<FiPlus className="w-4 h-4" />}
                  onClick={(e) => handleEditCategory()}>
                  Add Category
                </Button>
              </div>
              <div className="flex flex-col">
                {data.asset_categories.map((category) => (
                  <div
                    key={category._id}
                    id={`SettingsPage__category-${category._id}`}
                    className="flex gap-2 py-1 items-center group/category justify-between">
                    <div className="flex items-center gap-1">
                      <span className="text-secondary-foreground text-sm capitalize min-w-64">
                        {category.name}
                      </span>
                      {/* {category.allowed_types.map(type => (
                                                <span key={type} className='bg-secondary rounded-md text-secondary-foreground text-xs p-1'>{type}</span>
                                            ))} */}
                      {category.allow_wsc_import && (
                        <span className="bg-secondary rounded-md text-secondary-foreground text-xs p-1">
                          WSC Import
                        </span>
                      )}
                      {category.vectorize && (
                        <span className="bg-secondary rounded-md text-secondary-foreground text-xs p-1">
                          Vectorize
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover/category:opacity-100">
                      <Button
                        type="secondary"
                        icon={<FiEdit className="w-4 h-4" />}
                        onClick={() => handleEditCategory(category)}
                      />
                      <Button
                        id={`SettingsPage__deleteCategory-${category._id}`}
                        type="danger"
                        icon={<FiTrash className="w-4 h-4" />}
                        onClick={() =>
                          handleCategoryDelete(category._id.toString())
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end p-4 gap-4 ">
        <Button
          type="primary"
          disabled={!canSave}
          className="min-w-20"
          onClick={handleSave}>
          Save
        </Button>
      </div>
      {updating && <Loader status={loadingStatus} />}
    </div>
  );
}
